import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, Observable, tap, throwError, firstValueFrom } from 'rxjs';
import { Trip, TripBasicInfo } from '../models/trip';
import { Router } from '@angular/router';
import { ApiError, UploadImageResponse } from '../models/api-response';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TranslocoService } from '@jsverse/transloco';
import { Transport } from '../models/transport';
import { Activity } from '../models/activity';
import { Accommodation } from '../models/accommodation';
import { Waste } from '../models/waste';
import { Meal } from '../models/meal';
import { ValidationReport } from '../types/validation';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';

export interface CreateTripResponse {
  trip: Trip;
  impactId: string;
}

export interface TripResponse {
  trip: Trip;
  impactId: string;
}

@Injectable({
  providedIn: 'root',
})
export class TripService {
  trip = signal<Trip | null>(null);
  validationReport = signal<ValidationReport | null>(null);
  isValidating = signal(false);

  hasAnyPrestations = computed(() => {
    return (
      (this.trip()?.transports.length ?? 0) > 0 ||
      (this.trip()?.accommodations.length ?? 0) > 0 ||
      (this.trip()?.activities.length ?? 0) > 0
    );
  });

  // Sorted prestations by first scheduled day (chronological order)
  // Returns items with their original index for edit/delete operations
  sortedTransports = computed(() => {
    const transports = this.trip()?.transports ?? [];
    return transports
      .map((transport, originalIndex) => ({ item: transport, originalIndex }))
      .sort((a, b) => {
        const dayA = a.item.scheduledDays?.[0] ?? Infinity;
        const dayB = b.item.scheduledDays?.[0] ?? Infinity;
        return dayA - dayB;
      });
  });

  sortedAccommodations = computed(() => {
    const accommodations = this.trip()?.accommodations ?? [];
    return accommodations
      .map((accommodation, originalIndex) => ({
        item: accommodation,
        originalIndex,
      }))
      .sort((a, b) => {
        const dayA = a.item.scheduledDays?.[0] ?? Infinity;
        const dayB = b.item.scheduledDays?.[0] ?? Infinity;
        return dayA - dayB;
      });
  });

  sortedActivities = computed(() => {
    const activities = this.trip()?.activities ?? [];
    return activities
      .map((activity, originalIndex) => ({ item: activity, originalIndex }))
      .sort((a, b) => {
        const dayA = a.item.scheduledDays?.[0] ?? Infinity;
        const dayB = b.item.scheduledDays?.[0] ?? Infinity;
        return dayA - dayB;
      });
  });

  tripLength = signal<number | null>(1);
  private http = inject(HttpClient);
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private currentUserService = inject(CurrentUserService);
  private apiUrl = `${environment.apiUrl}/trips`;

  /**
   * Full trip, with the flight and road stops that the impact report payload
   * leaves out. Saving a trip replaces those collections, so anything sent to
   * `updateTrip()` must come from here.
   */
  getTrip(id: string): Observable<Trip> {
    return this.http.get<Trip>(`${this.apiUrl}/${id}`);
  }

  public async loadTrip(id: string): Promise<void> {
    // Clear validation while loading new trip
    this.validationReport.set(null);

    this.getTrip(id).subscribe({
      next: async (trip: Trip) => {
        this.trip.set(trip);

        // Always trigger validation on page load to ensure fresh data
        await this.triggerValidation(trip.id);
      },
      error: (err) => {
        console.error('error while loading trip :', err);
      },
    });
  }

  /**
   * Trigger validation with loading state
   */
  async triggerValidation(tripId: string): Promise<void> {
    if (this.isValidating()) return; // Prevent double calls

    this.isValidating.set(true);
    try {
      const report = await firstValueFrom(
        this.http.post<ValidationReport>(
          `${this.apiUrl}/${tripId}/validate`,
          {},
        ),
      );
      this.validationReport.set(report);
    } catch (error) {
      console.error('Validation failed:', error);
      // Keep old report, don't clear
    } finally {
      this.isValidating.set(false);
    }
  }

  /**
   * Auto-validate after any modification (with debounce in form services)
   */
  async autoValidate(): Promise<void> {
    const trip = this.trip();
    if (!trip?.id) return;

    await this.triggerValidation(trip.id);
  }

  addTransport(transport: Transport) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      transports: [...trip.transports, transport],
    };
    return this.updateAsDraft(tripToUpdate);
  }
  updateTransport(transport: Transport, index: number) {
    const trip = this.trip();
    if (!trip) return;

    const tripToUpdate = {
      ...trip,
      transports: trip.transports.map((t, i) => (i === index ? transport : t)),
    };

    return this.updateAsDraft(tripToUpdate);
  }

  deleteTransport(index: number) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      transports: trip.transports.filter((_, i) => i !== index),
    };
    return this.updateAsDraft(tripToUpdate);
  }

  addAccommodation(accommodation: Accommodation) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      accommodations: [...trip.accommodations, accommodation],
    };
    return this.updateAsDraft(tripToUpdate);
  }
  updateAccommodation(accommodation: Accommodation, index: number) {
    const trip = this.trip();
    if (!trip) return;

    const tripToUpdate = {
      ...trip,
      accommodations: trip.accommodations.map((t, i) =>
        i === index ? accommodation : t,
      ),
    };

    return this.updateAsDraft(tripToUpdate);
  }

  deleteAccommodation(index: number) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      accommodations: trip.accommodations.filter((_, i) => i !== index),
    };
    return this.updateAsDraft(tripToUpdate);
  }

  addMeal(meal: Meal) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      meals: [...trip.meals, meal],
    };
    return this.updateAsDraft(tripToUpdate);
  }

  updateMeal(meal: Meal, index: number) {
    const trip = this.trip();
    if (!trip) return;

    const tripToUpdate = {
      ...trip,
      meals: trip.meals.map((t, i) => (i === index ? meal : t)),
    };

    return this.updateAsDraft(tripToUpdate);
  }

  deleteMeal(index: number) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      meals: trip.meals.filter((_, i) => i !== index),
    };
    return this.updateAsDraft(tripToUpdate);
  }

  updateWaste(waste: Waste) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      wasteType: waste.type,
      kgOfPlastic: waste.kgOfPlastic,
      kgOfNonPlastic: waste.kgOfNonPlastic,
    };
    return this.updateAsDraft(tripToUpdate);
  }

  deleteWaste() {
    const trip = this.trip();
    if (!trip) {
      console.error('no trip found');
      return;
    }
    const tripToUpdate = {
      ...trip,
      wasteType: undefined,
      kgOfPlastic: undefined,
      kgOfNonPlastic: undefined,
    };
    return this.updateAsDraft(tripToUpdate);
  }

  addActivity(activity: Activity) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      activities: [...trip.activities, activity],
    };
    return this.updateAsDraft(tripToUpdate);
  }
  updateActivity(activity: Activity, index: number) {
    const trip = this.trip();
    if (!trip) return;

    const tripToUpdate = {
      ...trip,
      activities: trip.activities.map((t, i) => (i === index ? activity : t)),
    };

    return this.updateAsDraft(tripToUpdate);
  }

  deleteActivity(index: number) {
    const trip = this.trip();
    if (!trip) return;
    const tripToUpdate = {
      ...trip,
      activities: trip.activities.filter((_, i) => i !== index),
    };
    return this.updateAsDraft(tripToUpdate);
  }

  createTrip(trip: TripBasicInfo | Trip): Observable<CreateTripResponse> {
    return this.http
      .post<CreateTripResponse>(`${this.apiUrl}`, {
        ...trip,
        image: undefined,
      })
      .pipe(
        tap(() => this.currentUserService.refreshCurrentUser()),
        catchError((error) => {
          console.error('error creating trip', error);
          this.handleSaveTripError(error as ApiError);
          return throwError(() => error);
        }),
      );
  }

  createDraft(
    trip: TripBasicInfo | Trip,
    documentAnalysisJobId?: string,
  ): Observable<Trip> {
    return this.http
      .post<Trip>(`${environment.apiUrl}/drafts`, {
        ...trip,
        documentAnalysisJobId,
        image: undefined,
      })
      .pipe(
        tap(() => this.currentUserService.refreshCurrentUser()),
        catchError((error) => {
          console.error('error creating trip', error);
          this.handleSaveTripError(error as ApiError);
          return throwError(() => error);
        }),
      );
  }

  uploadTripImage(image: File) {
    const formData = new FormData();
    formData.append('image', image);
    const tripId = this.trip()?.id;
    this.http
      .post<UploadImageResponse>(`${this.apiUrl}/${tripId}/image`, formData)
      .subscribe({
        next: (response: UploadImageResponse) => {
          this.trip.update((trip) => {
            if (!trip) return trip;
            return {
              ...trip,
              imagePath: response.imagePath,
            };
          });
        },
        error: (error) => {
          console.error(error);
          const messageErrorSaving = this.translocoService.translate(
            'calculator.errorSavingImg',
          );
          this.notificationService.error(messageErrorSaving);
        },
      });
  }

  updateTrip(trip: Trip): Observable<TripResponse> {
    return this.http
      .put<TripResponse>(`${this.apiUrl}/${trip.id}`, {
        ...trip,
        image: undefined,
      })
      .pipe(
        catchError((error) => {
          console.error('error updating trip', error);
          this.handleSaveTripError(error as ApiError);
          return throwError(() => error);
        }),
        tap((response) => {
          this.trip.set(response.trip);
          this.notificationService.success(
            this.translocoService.translate('confirmation.tripSaved'),
          );
        }),
      );
  }

  updateAsDraft(trip: Trip): Observable<Trip> {
    console.log('updating as draft', trip);
    return this.http
      .put<Trip>(`${environment.apiUrl}/draft/${trip.id}`, {
        ...trip,
        image: undefined,
      })
      .pipe(
        catchError((error) => {
          console.error('error updating trip', error);
          // The API error handler strips the field list from validation errors,
          // so a missing country (unresolved by the AI extraction) is inferred
          // from the payload rather than read from the response.
          if (
            (error as ApiError).error?.code === 'E_VALIDATION_EXCEPTION' &&
            !trip.countryCode
          ) {
            this.notificationService.error(
              this.translocoService.translate('validation.countryMissingOnSave'),
            );
          } else {
            this.handleSaveTripError(error as ApiError);
          }
          return throwError(() => error);
        }),
        tap((trip: Trip) => {
          this.trip.set(trip);
          this.notificationService.success(
            this.translocoService.translate('confirmation.tripSaved'),
          );

          // Trigger validation after save completes (with debounce)
          setTimeout(() => {
            this.autoValidate();
          }, 500);
        }),
      );
  }

  navigateToTripDashboard(id: string) {
    this.router.navigate([`/home/trips/${id}/dashboard`]);
  }

  duplicateTrip(id: string): Observable<Trip> {
    return this.http.post<Trip>(`${this.apiUrl}/${id}/duplicate`, {}).pipe(
      catchError((error) => {
        console.error('error duplicating trip', error);
        this.handleSaveTripError(error as ApiError);
        return throwError(() => error);
      }),
      tap((trip: Trip) => {
        this.notificationService.success(
          this.translocoService.translate('confirmation.tripDuplicated'),
        );
        if (trip.id) this.navigateToTripDashboard(trip.id);
      }),
    );
  }

  handleSaveTripError(error: ApiError) {
    const errCode = error.error.code;
    if (errCode === 'E_GOOGLEMAPS_ZERO_RESULTS') {
      this.handleItinararyNotFoundError(error);
    } else if (errCode === 'E_TIM_ZERO_RESULTS') {
      this.notificationService.error(error.error.message);
    } else {
      const messageErrorSaving = this.translocoService.translate(
        'calculator.errorSaving',
      );
      this.notificationService.error(messageErrorSaving);
    }
  }

  private handleItinararyNotFoundError(error: ApiError) {
    let messageNoRouteFound = this.translocoService.translate(
      'calculator.errorNoRouteFound',
    );
    let errorMessage = error.error.message;
    const prefix = 'No results found for the given road stops: ';
    if (errorMessage.startsWith(prefix)) {
      errorMessage = errorMessage.slice(prefix.length);
      messageNoRouteFound += ` : ${errorMessage}`;
    }
    this.notificationService.error(messageNoRouteFound);
  }
}
